/**
 * Alexa Skill Terms Section - Comprehensive terms for Alexa integration
 */

import React from 'react';

export const AlexaSkillSection: React.FC = () => (
  <section>
    <h2 className="text-xl font-semibold mb-3">8. Alexa Skill Terms</h2>
    
    <div className="space-y-4 text-gray-600">
      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Acceptance of Terms</h3>
        <p>By enabling and using the DinnerBrain Alexa skill (&ldquo;the Skill&rdquo;), you agree to these additional terms governing the Skill, along with DinnerBrain&apos;s general Terms of Service. You also agree to Amazon&apos;s Alexa Terms of Use, which govern your use of Alexa services.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Service Description</h3>
        <p>The DinnerBrain Alexa skill allows you to add items to your DinnerBrain shopping list using voice commands through Alexa-enabled devices. This is a convenience feature and does not replace full access to DinnerBrain&apos;s web or mobile applications.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Account Requirements</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>You must have an active DinnerBrain account to use the Skill</li>
          <li>You must complete account linking through the Alexa app before using the Skill</li>
          <li>You are responsible for maintaining the security of both your DinnerBrain and Amazon accounts</li>
          <li>One DinnerBrain account can be linked to one Alexa account at a time</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Acceptable Use</h3>
        <p className="mb-2">When using the Skill, you agree to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Use the Skill only for personal, non-commercial purposes</li>
          <li>Provide accurate information when adding items to your shopping list</li>
          <li>Not attempt to override, bypass, or manipulate the Skill&apos;s functionality</li>
          <li>Not use the Skill for any illegal or unauthorized purpose</li>
          <li>Comply with all applicable laws and regulations</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Functionality and Limitations</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>The Skill allows adding items to your shopping list only</li>
          <li>Removing items, editing quantities, or managing lists requires using the DinnerBrain app or website</li>
          <li>The Skill requires an active internet connection to function</li>
          <li>Voice commands are processed by Amazon before reaching DinnerBrain</li>
          <li>We do not guarantee 100% accuracy in voice recognition or item categorization</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">No Warranty</h3>
        <p className="mb-2">The Skill is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, either express or implied. DinnerBrain does not warrant that:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The Skill will be uninterrupted or error-free</li>
          <li>Voice commands will always be accurately interpreted</li>
          <li>Items will always be correctly categorized</li>
          <li>The Skill will be compatible with all Alexa devices or features</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Limitation of Liability</h3>
        <p className="mb-2">DinnerBrain shall not be liable for:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Misinterpretation of voice commands by Amazon&apos;s voice recognition</li>
          <li>Duplicate items added to your shopping list</li>
          <li>Any indirect, incidental, or consequential damages arising from Skill use</li>
          <li>Interruptions or discontinuation of the Skill service</li>
          <li>Changes to Alexa that affect Skill functionality</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Third-Party Service</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>The Skill operates through Amazon&apos;s Alexa service</li>
          <li>DinnerBrain is not responsible for Alexa service availability or performance</li>
          <li>Amazon may change Alexa functionality at any time, which may affect the Skill</li>
          <li>Your use of Alexa is governed by Amazon&apos;s separate terms and policies</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Privacy and Data</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>Voice interactions are processed according to our Privacy Policy and Amazon&apos;s Privacy Policy</li>
          <li>DinnerBrain receives only text interpretations of your commands, not voice recordings</li>
          <li>You consent to the data sharing necessary for the Skill to function</li>
          <li>You can revoke consent by unlinking accounts or disabling the Skill</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Modifications and Termination</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>We may modify, suspend, or discontinue the Skill at any time without notice</li>
          <li>We may update these terms at any time; continued use constitutes acceptance</li>
          <li>You may stop using the Skill at any time by disabling it in the Alexa app</li>
          <li>Account termination in either DinnerBrain or Amazon will end Skill access</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Indemnification</h3>
        <p className="mb-2">You agree to indemnify and hold harmless DinnerBrain from any claims, damages, or expenses arising from:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your use or misuse of the Skill</li>
          <li>Your violation of these terms</li>
          <li>Your violation of any rights of another party</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Dispute Resolution</h3>
        <p>Any disputes related to the Skill shall be resolved according to the dispute resolution provisions in DinnerBrain&apos;s main Terms of Service, including the governing law and jurisdiction clauses outlined in Section 14.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Intellectual Property</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>The Skill and all related content remain the property of DinnerBrain</li>
          <li>&ldquo;Alexa&rdquo; and all related logos are trademarks of Amazon.com, Inc.</li>
          <li>You may not use our Skill name or branding for any unauthorized purpose</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">No Commercial Use</h3>
        <p>The Skill is for personal use only. Commercial use, including but not limited to resale of the service or use for business inventory management, is prohibited without written permission.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Severability</h3>
        <p>If any provision of these terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-700 mb-2">Disclaimer</h3>
        <p>DinnerBrain is not affiliated with, endorsed by, or sponsored by Amazon.com, Inc. or its affiliates.</p>
      </div>
    </div>
  </section>
);